import React from 'react';
import { Spin, Alert, Typography } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';
import { PromptBanner } from '../../../../../types/brands';
import { useChatMessages } from '../../../hooks/chat/use-chat-messages';
import { useSendChatMessage } from '../../../hooks/chat/use-send-chat-message';
import { usePolling } from '../../../hooks/use-polling';
import { useAITyping } from '../../../hooks/chat/use-ai-typing';
import { useBanners } from '../../../context/banners';
import { useModal } from '../../../context/modal';
import { ChatMessage } from './chat-message';
import { ChatInput } from './chat-input';
import { AITypingIndicator } from './ai-typing-indicator';
import { useAuthContext } from '../../../context/auth';
import { useResponsiveSizes } from '../../../hooks/use-responsive-sizes';

interface ChatPanelProps {
  banner: PromptBanner;
}

export const ChatPanel: React.FC<ChatPanelProps> = ({ banner }) => {
  const { spacing } = useResponsiveSizes();
  const [localWaitingForResponse, setLocalWaitingForResponse] = React.useState(false);

  const { refreshAllBanners } = useBanners();
  const { waitingForAI, setWaitingForAI } = useModal();
  const { messages, loading, error, refetch } = useChatMessages(banner.bannerId);
  const { sendMessage, isSending, error: sendError } = useSendChatMessage(banner.bannerId);
  const showAITyping = useAITyping({
    banner,
    localWaitingForResponse,
    setLocalWaitingForResponse,
    messages,
    isSending
  });

  const messagesContainerRef = React.useRef<HTMLDivElement>(null);
  const lastMsgCountRef = React.useRef(0);
  const stickToBottomRef = React.useRef(true);

  const { userInfo } = useAuthContext();
  const username = userInfo?.email?.split('@')[0]?.split('.')[0] || 'VOS';

  usePolling({
    onPoll: () => {
      refetch();
      refreshAllBanners();
    },
    enabled: waitingForAI
  });

  const handleSendMessage = async (prompt: string) => {
    const trimmedPrompt = prompt.trim();
    setLocalWaitingForResponse(true);
    const result = await sendMessage(trimmedPrompt);
    if (result) {
      setTimeout(() => refetch(), 500);
    }
  };

  // Actualizar estado de espera según el banner
  React.useEffect(() => {
    if (banner.status === 'PENDING' || banner.status === 'IN_PROGRESS') {
      setWaitingForAI(true);
      return;
    }

    if (banner.status === 'ERROR') {
      setWaitingForAI(false);
      return;
    }
  }, [messages, banner.status, setWaitingForAI]);

  // Scroll automático
  const scrollToBottom = (smooth: boolean = false) => {
    if (!messagesContainerRef.current) return;

    if (smooth) {
      messagesContainerRef.current.scrollTo({
        top: messagesContainerRef.current.scrollHeight,
        behavior: 'smooth'
      });
    } else {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Scroll cuando hay nuevos mensajes
  React.useEffect(() => {
    const isNewMessage = messages.length > lastMsgCountRef.current;
    if (isNewMessage && stickToBottomRef.current) {
      scrollToBottom(false);
    }
    lastMsgCountRef.current = messages.length;
  }, [messages]);

  // Scroll suave cuando aparece typing indicator
  React.useEffect(() => {
    if (showAITyping && stickToBottomRef.current) {
      scrollToBottom(true);
    }
  }, [showAITyping]);

  const handleMessagesScroll = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 40;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - threshold;
    stickToBottomRef.current = atBottom;
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%'
      }}
    >
      {/* Área de mensajes */}
      <div
        ref={messagesContainerRef}
        onScroll={handleMessagesScroll}
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          padding: `${spacing.sm}px 0`,
          borderBottom: '1px solid #f0f0f0',
          marginBottom: spacing.md,
          //scrollbarWidth: 'thin',
          scrollbarColor: '#e3e3e3 #FFFFFF',
          display: 'flex',
          flexDirection: 'column',
          scrollBehavior: 'smooth'
        }}
      >
        <div style={{ flex: 1, margin: '0 8px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {loading && messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: spacing.lg }}>
              <Spin indicator={<LoadingOutlined spin />} size='small' />
            </div>
          ) : (
            <>
              {messages.map(message => (
                <ChatMessage key={message.messageId} message={message} username={username} />
              ))}

              <AITypingIndicator show={showAITyping} />
            </>
          )}

          {error && (
            <Alert
              message='Error al cargar mensajes'
              description={error}
              type='error'
              style={{ margin: `${spacing.md}px 0` }}
            />
          )}
        </div>

        {/* Mensaje de estado para errores */}
        {banner.status === 'ERROR' && (
          <div
            style={{
              padding: `${spacing.sm}px 0`,
              textAlign: 'left',
              marginTop: 'auto'
            }}
          >
            <Typography.Text style={{ color: '#ff4d4f', fontSize: '11px', fontStyle: 'italic' }}>
              Hubo un error procesando tu solicitud. Intenta enviar otro mensaje.
            </Typography.Text>
          </div>
        )}
      </div>

      {/* Input de mensajes */}
      <div style={{ flexShrink: 0 }}>
        <ChatInput
          onSendMessage={handleSendMessage}
          disabled={isSending || localWaitingForResponse || loading}
          loading={isSending}
        />
        {sendError && <Alert message={sendError} type='error' style={{ marginTop: spacing.sm }} closable />}
      </div>
    </div>
  );
};
